import {
  CollectCampaignGlobalStateInterface,
  CreateTemplate,
  General,
  SetLos,
  SetOwners,
  SetUsers,
} from "@pages/Contents/Corporate/CollectCampaigns/types";
import React, { createContext, ReactNode, useContext, useReducer } from "react";

interface GlobalStateProviderProps {
  children: ReactNode;
}

type Action =
  | {
      type: "SET_INPUT_VALUES";
      payload: {
        key: string;
        value: any;
      };
    }
  | {
      type: "RESET_ALL_INPUT_VALUES";
    }
  | {
      type: "RESET_INPUT_VALUES";
      payload: (keyof CollectCampaignGlobalStateInterface)[];
    }
  | {
      type: "SET_GENERAL";
      payload: General;
    }
  | {
      type: "SET_LOS";
      payload?: SetLos;
    }
  | {
      type: "SET_OWNERS";
      payload?: SetOwners;
    }
  | {
      type: "SET_USERS";
      payload: SetUsers;
    }
  | {
      type: "SET_TEMPLATE";
      payload: {
        data?: CreateTemplate;
        zip?: File;
      };
    };

const CollectCampaignContext = createContext<
  | {
      state: CollectCampaignGlobalStateInterface;
      dispatch: React.Dispatch<Action>;
    }
  | undefined
>(undefined);

const initialState: CollectCampaignGlobalStateInterface = {};

const reducer = (
  state: CollectCampaignGlobalStateInterface,
  action: Action,
): CollectCampaignGlobalStateInterface => {
  switch (action.type) {
    case "SET_INPUT_VALUES":
      return {
        ...state,
        [action.payload!.key]: action.payload!.value,
      };
    case "RESET_ALL_INPUT_VALUES":
      return initialState;
    case "RESET_INPUT_VALUES":
      const newState: CollectCampaignGlobalStateInterface = { ...state };
      action.payload.forEach(
        (keyToReset: keyof CollectCampaignGlobalStateInterface) => {
          (newState as any)[keyToReset] = initialState[keyToReset];
        },
      );
      return newState;
    case "SET_GENERAL":
      const newStateGeneral = { ...state };
      newStateGeneral.general = {
        ...newStateGeneral.general,
        ...action.payload,
      };
      return newStateGeneral;
    case "SET_LOS":
      const newStateLos = { ...state };
      newStateLos.learning_objects = action.payload;
      return newStateLos;
    case "SET_OWNERS":
      const newStateOwners = { ...state };
      newStateOwners.owners = action.payload;
      return newStateOwners;
    case "SET_USERS":
      const newStateUsers = { ...state };
      newStateUsers.users = action.payload;
      return newStateUsers;
    case "SET_TEMPLATE":
      const newStateTemplate = { ...state };
      newStateTemplate.template = action.payload;
      return newStateTemplate;
    default:
      return state;
  }
};

export const CollectCampaignStateProvider = ({
  children,
}: GlobalStateProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <CollectCampaignContext.Provider value={{ state, dispatch }}>
      {children}
    </CollectCampaignContext.Provider>
  );
};

export const useCollectCampaignState = () => {
  const context = useContext(CollectCampaignContext);
  if (!context) {
    throw new Error(
      "useCollectCampaignState must be used within a GlobalStateProvider",
    );
  }
  return context;
};
